import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';
import { PageViewMode } from '../utils/page-view-mode';

// ─── JEMS3 page view mode (Normal / Amplo / Full) ──────────────────────────
// Lê `?viewMode=` do URL e expõe o modo atual reativamente.
// Templates consomem via `isFull$` / `isAmplo$` (com `| async`) e usam `*ngIf`
// para NÃO instanciar componentes de chrome quando em modo Full — evita
// polling/subscriptions/render desnecessários (era o que `display: none` no
// CSS deixava rodando sem aparecer).
// Definição dos modos: src/app/utils/page-view-mode.ts
// ───────────────────────────────────────────────────────────────────────────

@Injectable({ providedIn: 'root' })
export class ViewModeService {
  private readonly mode$ = new BehaviorSubject<PageViewMode>(PageViewMode.Normal);

  readonly viewMode$: Observable<PageViewMode> = this.mode$.asObservable();
  readonly isFull$: Observable<boolean> = this.viewMode$.pipe(
    map(m => m === PageViewMode.Full),
    distinctUntilChanged(),
  );
  readonly isAmplo$: Observable<boolean> = this.viewMode$.pipe(
    map(m => m === PageViewMode.Amplo),
    distinctUntilChanged(),
  );

  constructor(private router: Router) {
    this.update();
    this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe(() => this.update());

    // Mantém o body class sincronizado com o modo atual (algumas regras de
    // layout em _view-mode.scss precisam disso — margens/CSS vars que não
    // dá pra resolver via *ngIf no template).
    this.viewMode$.subscribe(mode => {
      document.body.classList.toggle('view-mode-full', mode === PageViewMode.Full);
      document.body.classList.toggle('view-mode-amplo', mode === PageViewMode.Amplo);
    });
  }

  get current(): PageViewMode {
    return this.mode$.value;
  }

  get isFull(): boolean {
    return this.mode$.value === PageViewMode.Full;
  }

  get isAmplo(): boolean {
    return this.mode$.value === PageViewMode.Amplo;
  }

  private update(): void {
    const query = this.router.url.split('?')[1] || '';
    const params = new URLSearchParams(query);
    const raw = params.get('viewMode');
    const mode = raw === PageViewMode.Full ? PageViewMode.Full
               : raw === PageViewMode.Amplo ? PageViewMode.Amplo
               : PageViewMode.Normal;
    if (mode !== this.mode$.value) {
      this.mode$.next(mode);
    }
  }
}
